<template>
  <v-select
    :value="loadedInitOptions ? value : null"
    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
    :label="label || computedLabel"
    :disabled="readonly"
    :placeholder="placeholder"
    :options="processedOptions"
    :filterBy="filterBy"
    :loading="loading"
    :clearable="clearable"
    :reduce="item => (reduce ? item[selectionKey] : item)"
    :get-option-label="getLabel"
    :class="{ 'disable-hover': disableHover }"
    :selectable="selectable"
    v-bind="$attrs"
    @search="handleSearch"
    @open="open"
    @close="handleClose"
    @search:blur="handleBlur"
    @input="input"
    @clear="handleClear"
  >
    <!-- <template #selected-option="option">
      <span>{{ formatLabel(option) }}</span>
    </template> -->

    <!-- Add new option hook -->
    <template #list-header>
      <div
        v-if="showAddNew"
        class="d-flex align-items-center py-1 justify-content-center cursor-pointer text-primary"
        @click.stop="handleAddNew"
      >
        <feather-icon
          icon="PlusCircleIcon"
          size="18"
          class="mr-50"
        />
        <span class="font-weight-bolder">{{ $t('button.addNew') }}</span>
      </div>
    </template>

    <template #option="option">
      <div
        v-if="option.isGroupLabel"
        class="group-label"
      >
        {{ formatLabel(option) }}
      </div>
      <span v-else>{{ formatLabel(option) }}</span>
    </template>
    <template
      v-for="(_, slot) of $scopedSlots"
      v-slot:[slot]="scope"
    >
      <slot
        :name="slot"
        v-bind="scope"
      />
    </template>
    <template #no-options>
      {{ $t('selection.noOption') }}
    </template>
    <template #spinner="{ loading }">
      <b-spinner
        v-if="loading"
        variant="primary"
        label="Spinning"
      />
    </template>
  </v-select>
</template>

<script>
import vSelect from 'vue-select'
import { BSpinner } from 'bootstrap-vue'

import RepositoryFactory from '@/repositories/RepositoryFactory'

export default {
  components: {
    vSelect,
    BSpinner,
  },
  props: {
    showAddNew: {
      type: Boolean,
      default: false,
    },
    placeholder: {},
    name: {},
    disabled: {},
    readonly: {},
    repository: {},
    value: {},
    clearable: {
      type: Boolean,
      default: false,
    },
    disableHover: {
      type: Boolean,
      default: false,
    },
    reduce: {
      type: Boolean,
      default: true,
    },
    selectionKey: {
      type: String,
      default: 'id',
    },
    selectionLabel: {
      type: [String, Array],
      default: 'name',
    },
    labelSeparator: {
      type: String,
      default: ' ',
    },
    labelWrapper: {
      type: Object,
      default: () => ({
        start: '(',
        end: ')',
      }),
    },
    query: {
      type: Object,
      default() {
        return {}
      },
    },
    excludeKey: {
      type: String,
      default: '',
    },
    excludes: {
      type: Array,
      default() {
        return []
      },
    },
    excludeOptions: {
      type: Array,
      default() {
        return []
      },
    },
    alwaysExcluded: {
      default: '',
    },
    isCustomParams: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: '',
    },
    immediate: {
      type: Boolean,
      default: true,
    },
    isObject: {
      type: Boolean,
      default: false,
    },
    mustExcludedIds: {},
    alwaysFresh: {
      type: Boolean,
      default: false,
    },
    dependencyKey: {
      type: String,
      default: '',
    },
    dependencyValue: {
      type: String,
      default: '',
    },
    groupKey: {
      type: String,
      default: '',
    },
    selectable: {
      type: Function,
      default: option => !option?.isGroupLabel,
    },
    disabledDisplayGroup: {
      type: Boolean,
      default: false,
    },
    isCached: {
      type: Boolean,
      default: false,
    },
    suffixLabel: {
      type: String,
      default: '',
    },
    autoSelectFirst: {
      type: Boolean,
      default: false,
    },
    autoSelectIsOwner: {
      type: Boolean,
      default: false,
    },
    isServerFiltered: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      option: [],
      Repository: null,
      loading: false,
      loadedInitOptions: false,
      searchKeyword: '',
    }
  },
  computed: {
    computedLabel() {
      if (!Array.isArray(this.selectionLabel)) return this.selectionLabel

      return this.selectionLabel[0]
    },
    processedOptions() {
      // If no groupKey is provided, return options as is
      if (!this.groupKey || !this.option.length) {
        return this.option
      }

      // Create a map to group options by the groupKey
      const groups = {}
      const result = []

      // Group options by the groupKey
      this.option.forEach(item => {
        // Skip items that don't have the groupKey
        if (!item[this.groupKey]) {
          result.push(item)
          return
        }

        // Extract the group object based on the groupKey
        const groupObj = item[this.groupKey]

        // Get group identifier (use code for service objects)
        const groupId = groupObj.code || groupObj.id || groupObj

        // Extract the group display name from the object
        let groupName
        if (groupObj.name) {
          if (typeof groupObj.name === 'object') {
            const locale = this.$i18n.locale.split('-')[0]
            groupName = groupObj.name[locale] || groupObj.name.en || groupObj.name.zh
                      || groupObj.name.km || groupObj.name.vi || groupId
          } else {
            groupName = groupObj.name
          }
        } else {
          groupName = groupId
        }

        // Check if we've already added this group label
        if (!groups[groupId]) {
          // Create group label item (non-selectable)
          const groupLabel = {
            [this.selectionKey]: `group_${groupId}`,
            [this.selectionLabel]: groupName,
            isGroupLabel: true,
            selectable: false,
          }

          result.push(groupLabel)
          groups[groupId] = true
        }

        // Add the item to the result array
        result.push(item)
      })

      return result
    },
    firstEligibleOptionKey() {
      if (!Array.isArray(this.option) || this.option.length === 0) return null
      const first = this.option.find(item => !item.isGroupLabel)
      return first ? first[this.selectionKey] : null
    },
  },
  watch: {
    value(value) {
      if (value === null) {
        this.clear()
      }

      if (!this.loadedInitOptions && value) {
        this.getOption()
      }
    },
    '$i18n.locale': {
      handler() {
        if (this.selectionLabel === 'translations') {
          this.updateTranslations()
        }
      },
      immediate: true,
    },
    query: {
      handler(newQuery, oldQuery) {
        if (
          JSON.stringify(newQuery) !== JSON.stringify(oldQuery)
            && !this.value
        ) {
          this.clear()
          this.getOption()
        }
      },
      deep: true,
    },
  },
  created() {
    this.Repository = RepositoryFactory.get(this.repository)
    if (this.immediate) this.getOption()
  },
  methods: {
    // Custom filter to optionally bypass client-side filtering when results come pre-filtered from server
    filterBy(option, label, search) {
      let text = label
      if (option?.translationsObj) {
        text = option?.translationsObj?.en + option?.translationsObj?.zh + option?.translationsObj?.km + option?.translationsObj?.vi + label
      }
      return (text + "" || '').toLocaleLowerCase().indexOf(search.toLocaleLowerCase()) > -1
    },
    handleClose() {
      this.$emit('blur')
    },
    handleBlur() {
      this.$emit('blur')
    },
    getLabel(option) {
      return this.findLabel(option)
    },
    handleAddNew() {
      console.log('>>> add new')
      this.$emit('add-new-hook')
      console.log('>>> emit add new')
    },
    handleClear() {
      console.log('clear')
      this.clear()
    },
    handleSearch(search) {
      if (search) {
        this.searchKeyword = search
        this.getOption()
      }
    },
    formatLabel(item) {
      if (!item) return ''

      // Special handling for group labels
      if (item.isGroupLabel) {
        // Handle both string and object name formats for group labels
        const label = item[this.selectionLabel]
        if (typeof label === 'object') {
          const locale = this.$i18n.locale.split('-')[0]
          return label[locale] || label.en || label.zh || label.km || label.vi || '-'
        }
        return label
      }

      if (Array.isArray(this.selectionLabel)) {
        return this.selectionLabel
          .map((label, index) => {
            const value = typeof item?.[label] === 'object'
              ? typeof item[label].name === 'object'
                ? item[label].name[this.$i18n.locale.split('-')[0]]
                    || item[label].name.en
                    || item[label].name.zh
                    || item[label].name.km
                    || item[label].name.vi
                    || '-'
                : item[label].name
              : item[label]
            if (index === 0) return value
            return `${this.labelWrapper.start}${value}${this.labelWrapper.end}`
          })
          .join(this.labelSeparator)
      }
      let baseLabel = item[this.selectionLabel]
      const isOwner = (item?.employee && item.employee.isOwner === true) || item?.isOwner === true
      if (this.suffixLabel && isOwner) {
        baseLabel = `${baseLabel} - ${this.suffixLabel}`
      }
      return baseLabel
    },
    findLabel(item) {
      if (item.hasOwnProperty(this.selectionKey)) {
        if (this.selectionLabel == 'translations' && item?.translationsObj) {
          let { locale } = this.$i18n
          if (locale == 'zh-cn') locale = 'zh'
          return (
            item.translationsObj[locale]
              || item.translationsObj.en
              || item.translationsObj.zh
              || item.translationsObj.km
              || item.translationsObj.vi
              || '-'
          )
        }
        return this.formatLabel(item)
      }
      this.clear()
      return ''
    },
    optionSelected() {
      // console.log("hi");
    },
    clear() {
      this.searchKeyword = ''
      this.option = []
      this.$emit('input', '')
      this.loadedInitOptions = false
    },
    input(value) {
      // this.$emit("input", value);
      const selectedObject = this.option.find(
        opt => opt[this.selectionKey] === value,
      )
      if (this.isObject) {
        this.$emit('input', selectedObject)
      } else {
        this.$emit('input', value)
      }
      this.$emit('change', selectedObject)
      this.optionChange(value)
    },
    optionChange(value) {
      const index = this.option.findIndex(item => item[this.selectionKey] === value)

      this.$emit('optionChange', this.option[index])
    },
    open() {
      if (this.isCached && this.loadedInitOptions && !this.alwaysFresh) return
      this.getOption()
    },
    mapExcludeIds() {
      const excludeIds = [...this.option]

      return excludeIds.map(item => item[this.selectionKey])
    },
    mergeArray(arrayA, arrayB) {
      return arrayB.concat(arrayA).reduce((accumulator, currentValue) => {
        if (
          !accumulator.some(item => (
            item[this.selectionKey] === currentValue[this.selectionKey]
          ))
        ) {
          accumulator.push(currentValue)
        }

        return accumulator
      }, [])
    },
    updateTranslations() {
      if (!this.option.length) return

      let { locale } = this.$i18n
      if (locale === 'zh-cn') locale = 'zh'

      this.option = this.option.map(item => ({
        ...item,
        translations:
            item?.translationsObj?.[locale]
            || item?.translationsObj?.en
            || item?.translationsObj.zh
            || item?.translationsObj.km
            || item?.translationsObj.vi
            || '-',
        translationsObj: item?.translationsObj,
      }))
    },
    getOption() {
      this.loading = true
      let { locale } = this.$i18n
      const needTranslate = this.selectionLabel == 'translations'
      if (locale == 'zh-cn') locale = 'zh'
      const excludeIds = this.mapExcludeIds()
      if (this.alwaysExcluded) {
        excludeIds.push(this.alwaysExcluded)
      }

      this.Repository.options({
        limit: 10,
        offset: 0,
        search: this.searchKeyword,
        includeIds: this.loadedInitOptions
          ? ''
          : this.reduce
            ? this.value
            : this.value[this.selectionKey],
        excludeIds: this.loadedInitOptions
          ? excludeIds.join(',')
          : this.alwaysExcluded,
        ...(this.mustExcludedIds
          ? { excludeIds: (this.mustExcludedIds || []).join(',') }
          : {}),
        ...this.query,
        ...(this.dependencyKey && this.dependencyValue
          ? { [this.dependencyKey]: this.dependencyValue }
          : {}),
        ...(this.isCustomParams && this.$route.params.departmentId
          ? { departmentId: this.$route.params.departmentId }
          : {}),
      })
        .then(response => {
          let items = []
          let list = Array.isArray(response?.data?.data?.list)
            ? response?.data?.data?.list
            : response.data.data

          if (list?.length && list[0]?.project && !this.disabledDisplayGroup) {
            const groupedByProject = list.reduce((acc, item) => {
              if (!item?.project?.id || !item?.project?.projectId) {
                // Handle items without project
                if (!acc.unassigned) {
                  acc.unassigned = {
                    title: 'Unassigned',
                    list: [],
                  }
                }
                acc.unassigned.list.push(item)
                return acc
              }

              const projectId = item.project.id
              if (!acc[projectId]) {
                acc[projectId] = {
                  title:
                      item.project?.name?.[this.$i18n.locale]
                      || item.project?.name?.en
                      || item.project?.name?.zh
                      || item.project?.name?.km
                      || item.project?.name?.vi
                      || 'Ungrouped',
                  list: [],
                }
              }
              acc[projectId].list.push(item)
              return acc
            }, {})

            const result = []
            Object.entries(groupedByProject)
              .sort((a, b) => a[1].title.localeCompare(b[1].title))
              .forEach(([projectId, group]) => {
                if (projectId !== 'unassigned') {
                  result.push({
                    id: projectId,
                    name: group.title,
                    isTitle: true,
                  })
                }
                result.push(...(group.list || []))
              })

            list = result
          }

          if (needTranslate) {
            list = list.map(e => ({
              ...e,
              translations:
                  e?.translation?.name?.[locale]
                  || e?.translation?.name?.en
                  || e?.translation?.name?.zh
                  || e?.translation?.name?.km
                  || e?.translation?.name?.vi
                  || e?.translations?.[locale]
                  || e?.translations?.en
                  || e?.translations?.zh
                  || e?.translations?.km
                  || e?.translations?.vi
                  || '-',
              translationsObj: e?.translations || e?.translation?.name,
            }))
            
          }
          if (this.alwaysFresh) {
            this.option = []
          }
          items = this.mergeArray(this.option, list)

          if (this.excludeKey && this.excludes.length > 0) {
            items = items.filter(
              i => !i[this.excludeKey]
                  || !this.excludes.includes(i[this.excludeKey]),
            )
          }
          // If suffixLabel passed, append to the first eligible item after mapping
          const mapOptionData = this.mergeArray(this.option, items)
          if (this.excludeOptions.length) {
            this.option = mapOptionData.filter(
              item => !this.excludeOptions.includes(item[this.selectionKey]),
            )
          } else {
            this.option = mapOptionData
          }

          // Auto select owner first if enabled; otherwise fallback to first option
          if (this.value === undefined || this.value === null || this.value === '') {
            if (this.autoSelectIsOwner) {
              const owner = this.option.find(item => item && !item.isGroupLabel && ((item?.employee && item.employee.isOwner === true) || item?.isOwner === true))
              if (owner && owner[this.selectionKey] !== undefined) {
                this.input(owner[this.selectionKey])
              }
            } else if (this.autoSelectFirst) {
              const first = this.option.find(item => item && !item.isGroupLabel)
              if (first && first[this.selectionKey] !== undefined) {
                this.input(first[this.selectionKey])
              }
            }
          }

          // Ensure firstEligibleOptionKey recomputes with latest options
          // No direct mutation needed; computed will pick up

          if (!this.loadedInitOptions) {
            /*
              // Find selected item from list
              const selectedItem = items.find(
                item =>
                  item[this.selectionKey] == this.value ||
                  item[this.selectionKey] == this.value[this.selectionKey]
              );
              if (!selectedItem) {
                this.clear();
              }
              */
            this.optionChange(this.value)
            this.loadedInitOptions = true
          }
          this.$emit('update:tmpList', this.option) /* emit tmpList to parent component with v-bind sync */
        })
        .catch()
        .then(() => {
          this.loading = false
        })
    },
  },
}
// inline
</script>

<style lang="scss">
  //external
  .vs__dropdown-toggle {
    svg {
      fill: #5e5873;
    }
  }

  //internal
  @import '@core/scss/vue/libs/vue-select.scss';

  .disable-hover {
    .vs__dropdown-menu {
      .vs__dropdown-option--highlight {
        background: transparent !important;
        color: inherit !important;
      }
    }
  }

  // Add styles for disabled state clear button
  .v-select.vs--disabled {
    .vs__clear {
      background: transparent !important;
    }
  }

  // Group label styling
  .group-label {
    font-size: 1rem;
    font-weight: 500;
    color: #7f7f7f;
    padding: 10px 10px 4px 10px;
    margin-top: 0;
    margin-bottom: 0;
    pointer-events: none;
    user-select: none;
    margin-left: -18px;
    border-top: 1px solid #e0e0e0;
  }
</style>

<style>
  .vs__dropdown-option--disabled:has(.group-label) {
    padding-top: 0 !important;
  }
</style>
